import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { signIn, sendMagicLink } from "@/lib/auth";
import { VerifyEmailNotice } from "@/components/auth/VerifyEmailNotice";
import {
  AuthShell,
  Field,
  GoldButton,
  PasswordInput,
  SocialAuthButtons,
  authInputClass,
  errorText,
} from "@/components/auth/AuthShell";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign In — CB16" },
      { name: "description", content: "Access your CB16 luxury perfume account." },
      { property: "og:title", content: "Sign In — CB16" },
      { property: "og:description", content: "Access your CB16 luxury perfume account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({ email: fe.email?.[0], password: fe.password?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error, code } = await signIn(parsed.data.email, parsed.data.password);
    setLoading(false);
    if (code === "email_not_confirmed") {
      setUnverifiedEmail(parsed.data.email);
      return;
    }
    if (error) {
      setErrors({ form: error });
      return;
    }
    const to = redirect && redirect.startsWith("/") ? redirect : "/";
    navigate({ to, replace: true });
  }

  async function onMagicLink() {
    setMagicSent(false);
    setMagicError(null);
    const parsed = z.string().trim().email().safeParse(email);
    if (!parsed.success) {
      setMagicError("Enter a valid email to receive a magic link");
      return;
    }
    setMagicLoading(true);
    const { error } = await sendMagicLink(parsed.data);
    setMagicLoading(false);
    if (error) {
      setMagicError(error);
      return;
    }
    setMagicSent(true);
  }

  if (unverifiedEmail) {
    return (
      <AuthShell title="Almost there" subtitle="Your email address has not been verified yet">
        <VerifyEmailNotice email={unverifiedEmail} onReturn={() => { setUnverifiedEmail(null); setPassword(""); }} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your CB16 account">
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="Email" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClass}
          />
        </Field>
        <Field label="Password" htmlFor="password" error={errors.password}>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
          />
        </Field>
        <div className="text-right text-xs tracking-wide text-black/70">
          <Link to="/forgot-password" className="hover:text-[#C6A15B]">
            Forgot password?
          </Link>
        </div>
        {errors.form && <p className={errorText}>{errors.form}</p>}
        <GoldButton type="submit" disabled={loading}>
          {loading ? "Signing in…" : "Sign In"}
        </GoldButton>
        <div className="flex items-center gap-3 py-1">
          <span className="h-px flex-1 bg-black/10" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-black/40">or</span>
          <span className="h-px flex-1 bg-black/10" />
        </div>
        <button
          type="button"
          onClick={onMagicLink}
          disabled={magicLoading}
          className="w-full border border-[#C6A15B] py-3 text-sm font-medium uppercase tracking-[0.2em] text-[#C6A15B] transition hover:bg-[#C6A15B]/10 disabled:opacity-60"
        >
          {magicLoading ? "Sending…" : "Continue with Magic Link"}
        </button>
        {magicSent && (
          <p className="text-center text-xs text-black/70">
            Check your email. We&apos;ve sent you a secure sign-in link.
          </p>
        )}
        {magicError && <p className={errorText}>{magicError}</p>}
        <SocialAuthButtons redirectPath={redirect} />
        <p className="text-center text-xs tracking-wide text-black/70">
          <Link to="/signup" className="hover:text-[#C6A15B]">
            Create account
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
